import React, { createContext, useContext, useState, useEffect } from 'react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { clearTokenCache } from '../utils/tokenUtils';

interface AuthContextType {
    msalInstance: PublicClientApplication;
    isAuthenticated: boolean;
    account: AccountInfo | null;
    setAccount: (account: AccountInfo | null) => void;
    logout: () => void;
    isLoading: boolean;
    authority: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: React.ReactNode;
    msalInstance: PublicClientApplication;
    authority: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, msalInstance, authority }) => {
    const [account, setAccount] = useState<AccountInfo | null>(msalInstance ? msalInstance.getActiveAccount() : null);
    const isAuthenticated = account !== null;
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) {
            setAccount(activeAccount);
        }

        setIsLoading(false);
    }, [msalInstance]);

    const logout = () => {
        msalInstance.setActiveAccount(null);
        localStorage.removeItem('msal.idtoken');
        localStorage.removeItem('msal.accessToken');
        clearTokenCache(); // Clear our custom token cache
        setAccount(null);
    };

    return (
        <AuthContext.Provider value={{ msalInstance, isAuthenticated, account, setAccount, logout, isLoading, authority }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};